// Duplicate detection when registering a patient (plan §4; workflow registration.md,
// scenario 4).
//
// Two kinds of result:
//
// - Blocking conflicts. Another active patient in the tenant already holds one of the
//   identifiers typed (same system, same normalised value, same issuing country for a
//   passport). An identifier belongs to one person, so this can't be overridden: the
//   receptionist uses that patient or corrects the number.
// - Candidates. Other patients who might be the same person, each with every reason that
//   matched. The receptionist must choose "Use this patient" or "This is a different
//   person" with a reason.
//
// The API finds the candidates' facts with SQL inside the tenant (trigram similarity is
// computed by Postgres, so it is the same number search uses) and hands them here; the
// rules themselves are pure so every one of them is unit-tested.
//
// Every threshold is a tenant setting (plan §12, decision 6). The defaults below are the
// plan's proposal, confirmed only as a starting point: they are operational, not
// clinical, and the settings screen labels them provisional.

import type { IdentifierSystem } from './identifiers.ts';
import { isTenantUniqueIdentifierSystem } from './identifiers.ts';
import type { CalendarDay } from './time.ts';
import { compareDays, daysInMonth, daysEqual } from './time.ts';

export interface DuplicateSettings {
  /** Trigram similarity (0–1) of the name keys at or above which names count as similar. */
  readonly nameSimilarityMin: number;
  /** When either birth date is estimated, dates this many years apart still count as close. */
  readonly estimatedBirthDateToleranceYears: number;
  /** Whether a shared phone number on its own raises a candidate. */
  readonly sharedPhoneRaisesCandidate: boolean;
}

/** Provisional defaults (plan §4.2, §12 decision 6). Tenants can change them. */
export const DEFAULT_DUPLICATE_SETTINGS: DuplicateSettings = {
  nameSimilarityMin: 0.6,
  estimatedBirthDateToleranceYears: 2,
  sharedPhoneRaisesCandidate: true,
};

/** "This is a different person" needs a reason at least this long. */
export const DUPLICATE_OVERRIDE_REASON_MIN_LENGTH = 5;

export const DUPLICATE_REASONS = [
  'same_phone',
  'similar_name_same_birth_date',
  'similar_name_close_birth_date',
  'same_name_same_principal',
] as const;

export type DuplicateReason = (typeof DUPLICATE_REASONS)[number];

export interface DraftIdentifier {
  readonly system: IdentifierSystem;
  /** From `normaliseIdentifier`. */
  readonly valueNormalised: string;
  /** ISO 3166-1 alpha-2, passports only. */
  readonly issuingCountry?: string | null | undefined;
}

/** What the receptionist has typed so far, already normalised. */
export interface RegistrationDraft {
  readonly birthDate: CalendarDay | null;
  readonly birthDateEstimated: boolean;
  readonly identifiers: readonly DraftIdentifier[];
  /** Normalised mobile numbers (`2547…`/`2541…`). */
  readonly phones: readonly string[];
  /** Set when registering a dependant of an existing patient. */
  readonly principalPatientId?: string | null | undefined;
}

/** Facts about one existing patient, gathered by the API within the tenant. */
export interface CandidateFacts {
  readonly patientId: string;
  /** `similarity(name_search, draft key)` from Postgres, 0–1. */
  readonly nameSimilarity: number;
  readonly birthDate: CalendarDay | null;
  readonly birthDateEstimated: boolean;
  /** The candidate's active identifiers. */
  readonly identifiers: readonly DraftIdentifier[];
  /** The candidate's active mobile numbers, normalised. */
  readonly phones: readonly string[];
  /** True when the candidate is already an active dependant of the draft's principal. */
  readonly isDependantOfDraftPrincipal: boolean;
}

export interface IdentifierConflict {
  readonly patientId: string;
  readonly system: IdentifierSystem;
}

export interface DuplicateCandidate {
  readonly patientId: string;
  /** Never empty; in the order of `DUPLICATE_REASONS`. */
  readonly reasons: readonly DuplicateReason[];
}

export interface DuplicateCheckResult {
  readonly blocking: readonly IdentifierConflict[];
  readonly candidates: readonly DuplicateCandidate[];
}

function sameIdentifier(a: DraftIdentifier, b: DraftIdentifier): boolean {
  return (
    a.system === b.system &&
    a.valueNormalised === b.valueNormalised &&
    (a.issuingCountry ?? null) === (b.issuingCountry ?? null)
  );
}

/** The same calendar day `years` later, with 29 February becoming 28 February. */
function addYears(day: CalendarDay, years: number): CalendarDay {
  const year = day.year + years;
  return { year, month: day.month, day: Math.min(day.day, daysInMonth(year, day.month)) };
}

/** Whether two dates are no more than `years` calendar years apart, in either order. */
export function withinYears(a: CalendarDay, b: CalendarDay, years: number): boolean {
  const [earlier, later] = compareDays(a, b) <= 0 ? [a, b] : [b, a];
  return compareDays(later, addYears(earlier, years)) <= 0;
}

function checkSettings(settings: DuplicateSettings): void {
  if (
    !Number.isFinite(settings.nameSimilarityMin) ||
    settings.nameSimilarityMin <= 0 ||
    settings.nameSimilarityMin > 1
  ) {
    throw new RangeError('nameSimilarityMin must be above 0 and at most 1');
  }
  if (
    !Number.isInteger(settings.estimatedBirthDateToleranceYears) ||
    settings.estimatedBirthDateToleranceYears < 0
  ) {
    throw new RangeError('estimatedBirthDateToleranceYears must be a whole number, 0 or more');
  }
}

/** The reasons one existing patient might be the person being registered. */
export function candidateReasons(
  draft: RegistrationDraft,
  candidate: CandidateFacts,
  settings: DuplicateSettings = DEFAULT_DUPLICATE_SETTINGS,
): DuplicateReason[] {
  checkSettings(settings);
  const reasons = new Set<DuplicateReason>();

  const similarName = candidate.nameSimilarity >= settings.nameSimilarityMin;
  const mine = draft.birthDate;
  const theirs = candidate.birthDate;
  const sameDate = mine !== null && theirs !== null && daysEqual(mine, theirs);
  const closeDate =
    mine !== null &&
    theirs !== null &&
    !sameDate &&
    (draft.birthDateEstimated || candidate.birthDateEstimated) &&
    withinYears(mine, theirs, settings.estimatedBirthDateToleranceYears);

  if (similarName && sameDate) reasons.add('similar_name_same_birth_date');
  if (similarName && closeDate) reasons.add('similar_name_close_birth_date');

  if (
    draft.principalPatientId != null &&
    candidate.isDependantOfDraftPrincipal &&
    similarName &&
    sameDate
  ) {
    reasons.add('same_name_same_principal');
  }

  const sharesPhone = draft.phones.some((phone) => candidate.phones.includes(phone));
  if (sharesPhone && (settings.sharedPhoneRaisesCandidate || reasons.size > 0)) {
    reasons.add('same_phone');
  }

  return DUPLICATE_REASONS.filter((reason) => reasons.has(reason));
}

/**
 * Runs every rule over the candidates the API found. Candidates keep the order they were
 * given in (the API orders them by patient number), so the list never reorders between
 * two identical checks. A patient with a blocking conflict is reported only as blocking.
 */
export function checkDuplicates(
  draft: RegistrationDraft,
  candidates: readonly CandidateFacts[],
  settings: DuplicateSettings = DEFAULT_DUPLICATE_SETTINGS,
): DuplicateCheckResult {
  checkSettings(settings);
  const blocking: IdentifierConflict[] = [];
  const found: DuplicateCandidate[] = [];
  const seen = new Set<string>();

  for (const candidate of candidates) {
    if (seen.has(candidate.patientId)) continue;
    seen.add(candidate.patientId);

    const conflicts = draft.identifiers.filter(
      (mine) =>
        isTenantUniqueIdentifierSystem(mine.system) &&
        candidate.identifiers.some((theirs) => sameIdentifier(mine, theirs)),
    );
    if (conflicts.length > 0) {
      for (const conflict of conflicts)
        blocking.push({ patientId: candidate.patientId, system: conflict.system });
      continue;
    }

    const reasons = candidateReasons(draft, candidate, settings);
    if (reasons.length > 0) found.push({ patientId: candidate.patientId, reasons });
  }

  return { blocking, candidates: found };
}

export type DuplicateResolution =
  | { readonly kind: 'none_found' }
  | { readonly kind: 'blocked' }
  | { readonly kind: 'needs_decision' };

/** What the registration screen has to do next with a check result. */
export function resolutionNeeded(result: DuplicateCheckResult): DuplicateResolution {
  if (result.blocking.length > 0) return { kind: 'blocked' };
  if (result.candidates.length > 0) return { kind: 'needs_decision' };
  return { kind: 'none_found' };
}

/**
 * Whether "This is a different person" may go ahead: no blocking conflicts, the reason is
 * long enough, and the override names every candidate the server found. A candidate that
 * appeared after the receptionist looked (another desk registered them meanwhile) has not
 * been considered, so the override does not cover it.
 */
export function overrideCovers(
  result: DuplicateCheckResult,
  override: { readonly candidateIds: readonly string[]; readonly reason: string },
): boolean {
  if (result.blocking.length > 0) return false;
  if (override.reason.trim().length < DUPLICATE_OVERRIDE_REASON_MIN_LENGTH) return false;
  return result.candidates.every((candidate) =>
    override.candidateIds.includes(candidate.patientId),
  );
}
